/**
 * Templates.jsx — route: /agreements/templates
 *
 * Every draftable template in the library, GROUPED BY TOPIC (category)
 * and rendered with the SAME visual language as the Library page:
 *   • Left-border-accented topic header with category icon, uppercase
 *     accent-tinted pill, count, name, and blurb.
 *   • `<DocumentCard>` rows underneath — same grid, same detail (parties,
 *     importance, attorney-review, template badge) — so scanning
 *     templates feels identical to scanning the library.
 *
 * Data flow: `templateLibrary.js` tags every template with `_source`
 * (category id). We match each template to its underlying document
 * from `DOCUMENTS` via `template.docId` and hand the doc to
 * `<DocumentCard>` — this reuses all the metadata / styling the Library
 * page already has instead of duplicating a card layout.
 *
 * No filters here on purpose — the Library page already carries the
 * heavy search/filter surface. This page is the fast path when a
 * founder knows they want a template and just wants to see what's
 * available under each topic.
 */
import React, { useMemo } from "react";
import AgreementsShell from "@/components/agreements/AgreementsShell";
import DocumentCard from "@/components/agreements/DocumentCard";
import CategoryIcon from "@/components/agreements/CategoryIcon";
import LegalDisclaimer from "@/components/agreements/LegalDisclaimer";
import { cn } from "@/lib/utils";
import { CATEGORIES, CATEGORY_BY_ID, ACCENT_CLASSES } from "@/data/agreements/categories";
import { DOCUMENTS } from "@/data/agreements/documentLibrary";
import { TEMPLATES } from "@/data/agreements/templateLibrary";

const DOC_BY_ID = Object.fromEntries(DOCUMENTS.map((d) => [d.id, d]));

export default function Templates() {
  // Group templates by their aggregator-injected `_source` (category id).
  // We iterate `CATEGORIES` in order so topics render in the same
  // sequence founders already see on the Library page — muscle memory
  // moves them across surfaces without hunting.
  const groups = useMemo(() => {
    const byCat = new Map();
    for (const t of TEMPLATES) {
      const catId = t._source || "uncategorized";
      // Prefer the real DOCUMENTS entry (has parties, importance,
      // attorney-review — everything DocumentCard needs). Fall back to
      // a minimal shim only if the doc isn't in the library so the row
      // still renders instead of silently disappearing.
      const doc =
        DOC_BY_ID[t.docId] ||
        {
          id: t.docId,
          name: t.title,
          category: catId,
          templateId: t.id,
        };
      if (!byCat.has(catId)) byCat.set(catId, []);
      byCat.get(catId).push(doc);
    }
    const seen = new Set();
    const ordered = [];
    for (const cat of CATEGORIES) {
      const docs = byCat.get(cat.id);
      if (docs && docs.length) {
        ordered.push({ category: cat, docs });
        seen.add(cat.id);
      }
    }
    for (const [catId, docs] of byCat.entries()) {
      if (!seen.has(catId)) {
        ordered.push({
          category: CATEGORY_BY_ID[catId] || { id: catId, name: catId, short: catId, blurb: "" },
          docs,
        });
      }
    }
    return ordered;
  }, []);

  return (
    <AgreementsShell
      wide
      eyebrow="Templates"
      title="Every draftable template, by topic."
      subtitle={`${TEMPLATES.length} starting templates across ${groups.length} topics — pick one, open the Founder Guide, then take it to your attorney with the negotiate-carefully points already highlighted.`}
      crumbs={[{ label: "Templates" }]}
    >
      <div data-testid="templates-groups">
        {groups.map((group) => (
          <TopicSection key={group.category.id} category={group.category} docs={group.docs} />
        ))}
      </div>

      <LegalDisclaimer variant="library" />
    </AgreementsShell>
  );
}

/**
 * TopicSection — mirrors the Library page's `groups.map(...)` header
 * so the two surfaces share exact visual DNA (left-border accent,
 * category icon, uppercase pill, count, title, blurb).
 */
function TopicSection({ category, docs }) {
  const accent = ACCENT_CLASSES[category.accent] || null;
  return (
    <section
      id={`cat-${category.id}`}
      className="mb-10 scroll-mt-24"
      data-testid={`templates-topic-${category.id}`}
    >
      <div
        className={cn(
          "mb-4 flex items-start gap-3 rounded-xl border-l-4 py-2 pl-3",
          accent?.border || "border-slate-500",
        )}
      >
        <CategoryIcon categoryId={category.id} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest ring-1",
                accent?.bg,
                accent?.text,
                accent?.ring,
              )}
            >
              <span className={cn("h-1.5 w-1.5 rounded-full", accent?.dot)} />
              {category.short || category.name}
            </span>
            <span className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">
              {docs.length} {docs.length === 1 ? "template" : "templates"}
            </span>
          </div>
          <h2 className="mt-1 text-xl font-bold tracking-tight text-neutral-900">
            {category.name}
          </h2>
          {category.blurb && <p className="mt-0.5 text-sm text-neutral-500">{category.blurb}</p>}
        </div>
      </div>
      <div className="card-grid-auto gap-3">
        {docs.map((doc) => (
          <DocumentCard key={doc.id} doc={doc} to={`/agreements/template/${doc.id}`} />
        ))}
      </div>
    </section>
  );
}
